// /src/components/SignupV3.js
// Componente Modal Signup V3 Editorial
// Sistema de diseño SFS 2.0

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, EyeOff, ArrowRight, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useI18next } from "gatsby-plugin-react-i18next";
import { EMAIL_FUNCTIONS_BASE, emailRequestHeaders } from "../utils/emailFunctions";
import { useAuth } from "../context/AuthContext";
import { useModal } from "../context/ModalContext";
import { useSchool } from "../context/SchoolContext";
import { db, doc, setDoc } from "../firebase";

// --- Icono de Google ---
const GoogleIcon = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const SignupV3 = ({ onClose }) => {
  const { t } = useTranslation();
  const { navigate } = useI18next();
  const { signup, signupWithGoogle } = useAuth();
  const { signupSource, switchToLogin } = useModal();
  const school = useSchool();
  const schoolId = school?.ai?.schoolId || 'sfs';

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  // Password strength calculator
  const handlePasswordChange = (e) => {
    const val = e.target.value;
    let strength = 0;
    if (val.length >= 8) strength += 1;
    if (/[A-Z]/.test(val)) strength += 1;
    if (/[0-9]/.test(val)) strength += 1;
    if (/[^A-Za-z0-9]/.test(val)) strength += 1;
    setPasswordStrength(strength);
    setPassword(val);
  };

  const getStrengthColor = () => {
    if (passwordStrength === 0) return 'bg-transparent';
    if (passwordStrength === 1) return 'bg-red-500';
    if (passwordStrength === 2) return 'bg-yellow-500';
    if (passwordStrength === 3) return 'bg-green-400';
    return 'bg-green-500';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Validation
    const trimFirst = firstName.trim();
    const trimLast = lastName.trim();
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    if (!trimFirst || !trimLast || !trimmedEmail || !trimmedPassword) {
      setError(t("auth.signup.errors.emptyFields"));
      setLoading(false);
      return;
    }

    const emailRegex = /^\S+@\S+\.\S+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError(t("auth.signup.errors.invalidEmail"));
      setLoading(false);
      return;
    }

    if (trimmedPassword.length < 8) {
      setError(t("auth.signup.errors.passwordTooShort"));
      setLoading(false);
      return;
    }

    try {
      console.log('[SignupV3] Starting email signup process...');

      console.log('[SignupV3] Creating auth user...');
      const userCredential = await signup(trimmedEmail, trimmedPassword, trimFirst, trimLast);
      const user = userCredential.user;
      console.log('[SignupV3] Auth user created:', user.uid);

      // Create user document in Firestore
      console.log('[SignupV3] Creating Firestore document...');
      await setDoc(doc(db, "students", user.uid), {
        fullName: `${trimFirst} ${trimLast}`.trim(),
        firstName: trimFirst,
        lastName: trimLast,
        email: trimmedEmail,
        plan: "new",
        acceptedMarketing: acceptMarketing,
        folderLink: "https://zoomdocs.com/your-default-folder",
        createdAt: new Date(),
        totalHours: 0,
        hoursPerWeek: 0,
        signupSource: signupSource || 'direct',
        signupMethod: 'email',
        source: "modal-signup-v3",
        sessions_balance: { guided: 0, freeChats: 0 }
      });
      console.log('[SignupV3] Firestore document created successfully');

      // Send welcome email (non-blocking)
      fetch(`${EMAIL_FUNCTIONS_BASE}/sendWelcomeEmail`, {
        method: 'POST',
        headers: await emailRequestHeaders(),
        body: JSON.stringify({
          email: trimmedEmail,
          firstName: trimFirst,
          lastName: trimLast,
          schoolId,
        })
      }).catch(emailError => {
        console.error("Welcome email error:", emailError);
      });

      onClose();
      navigate('/fluency-compass');
    } catch (err) {
      console.error("[SignupV3] Signup Error:", err);
      console.error("[SignupV3] Error code:", err.code);
      console.error("[SignupV3] Error message:", err.message);

      if (err.code === 'auth/email-already-in-use') {
        setError(t("auth.signup.errors.emailInUse"));
      } else {
        setError(t("auth.signup.errors.generalError"));
      }
    }
    setLoading(false);
  };

  const handleGoogleSignup = async () => {
    setError('');
    setLoading(true);
    try {
      console.log('[SignupV3] Starting Google signup...');
      const userCredential = await signupWithGoogle();
      const user = userCredential.user;
      console.log('[SignupV3] Google user authenticated:', user.uid);

      // Create user document in Firestore
      const firstName = user.displayName?.split(' ')[0] || user.email?.split('@')[0] || 'Student';
      const lastName = user.displayName?.split(' ').slice(1).join(' ') || '';

      console.log('[SignupV3] Creating Firestore document for Google user...');
      await setDoc(doc(db, "students", user.uid), {
        fullName: user.displayName || user.email,
        firstName,
        lastName,
        email: user.email,
        plan: "new",
        acceptedMarketing: acceptMarketing,
        folderLink: "https://zoomdocs.com/your-default-folder",
        createdAt: new Date(),
        totalHours: 0,
        hoursPerWeek: 0,
        signupSource: signupSource || 'direct',
        signupMethod: 'google',
        source: "google-signup-v3",
        sessions_balance: { guided: 0, freeChats: 0 }
      });
      console.log('[SignupV3] Firestore document created for Google user');

      // Send welcome email (non-blocking)
      fetch(`${EMAIL_FUNCTIONS_BASE}/sendWelcomeEmail`, {
        method: 'POST',
        headers: await emailRequestHeaders(),
        body: JSON.stringify({
          email: user.email,
          firstName,
          lastName,
          schoolId,
        })
      }).catch(emailError => {
        console.error("Welcome email error:", emailError);
      });

      onClose();
      navigate('/fluency-compass');
    } catch (err) {
      console.error("[SignupV3] Google Sign-Up Error:", err);
      setError(t("auth.signup.errors.googleError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {/* Overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-[#0F172A]/60 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 z-50 overflow-y-auto"
        onClick={onClose}
      >
        {/* Modal Box */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative overflow-hidden my-auto sm:my-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Decorative top accent */}
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#E76F51]/40 via-[#E76F51]/40 to-[#0F172A]/40" />

          {/* Close Button */}
          <button
            type="button"
            className="absolute top-4 right-4 p-2 rounded-xl text-brand-text/40 hover:text-[#0F172A] hover:bg-brand-light transition-all duration-200 z-10"
            onClick={onClose}
            aria-label={t("auth.signup.closeButton")}
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="p-6 md:p-7">
            {/* Header */}
            <div className="text-center mb-5">
              <div className="inline-flex items-center justify-center w-12 h-12 mb-3">
                <img
                  src="/images/Logo/Logo Icon SFS.png"
                  alt="SFS"
                  className="w-12 h-12"
                />
              </div>
              <h2 className="font-display text-xl md:text-2xl font-bold text-[#0F172A] mb-1 leading-tight">
                {t("auth.signup.title")}
              </h2>
              <p className="font-sans text-sm text-brand-text/60">
                {t("auth.signup.subtitle")}
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl"
              >
                <p className="font-sans text-sm text-red-700 text-center">{error}</p>
              </motion.div>
            )}

            {/* Google Button */}
            <button
              type="button"
              onClick={handleGoogleSignup}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-[#0F172A]/10 rounded-xl font-sans font-semibold text-[#0F172A] hover:border-[#0F172A]/30 hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
            >
              <GoogleIcon />
              <span>{t("auth.signup.googleButton")}</span>
              <span className="text-xs font-normal text-brand-text/50">{t("auth.signup.googleRecommended")}</span>
            </button>

            {/* Divider */}
            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-brand-text/10"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-3 bg-white font-sans text-xs text-brand-text/50">
                  {t("auth.signup.divider")}
                </span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              {/* First + Last name */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label htmlFor="signup-firstName" className="block font-sans text-xs font-semibold text-[#0F172A] mb-1">
                    {t("auth.signup.firstNameLabel")}
                  </label>
                  <input
                    id="signup-firstName"
                    type="text"
                    autoComplete="given-name"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={loading}
                    className="w-full px-3 py-2.5 bg-brand-light border-2 border-brand-text/10 rounded-lg font-sans text-sm text-[#0F172A] placeholder-brand-text/40 focus:outline-none focus:border-[#E76F51] focus:ring-2 focus:ring-[#E76F51]/20 transition-all duration-200"
                    placeholder={t("auth.signup.firstNamePlaceholder")}
                  />
                </div>
                <div>
                  <label htmlFor="signup-lastName" className="block font-sans text-xs font-semibold text-[#0F172A] mb-1">
                    {t("auth.signup.lastNameLabel")}
                  </label>
                  <input
                    id="signup-lastName"
                    type="text"
                    autoComplete="family-name"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={loading}
                    className="w-full px-3 py-2.5 bg-brand-light border-2 border-brand-text/10 rounded-lg font-sans text-sm text-[#0F172A] placeholder-brand-text/40 focus:outline-none focus:border-[#E76F51] focus:ring-2 focus:ring-[#E76F51]/20 transition-all duration-200"
                    placeholder={t("auth.signup.lastNamePlaceholder")}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="signup-email" className="block font-sans text-xs font-semibold text-[#0F172A] mb-1">
                  {t("auth.signup.emailLabel")}
                </label>
                <input
                  id="signup-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={loading}
                  className="w-full px-3 py-2.5 bg-brand-light border-2 border-brand-text/10 rounded-lg font-sans text-sm text-[#0F172A] placeholder-brand-text/40 focus:outline-none focus:border-[#E76F51] focus:ring-2 focus:ring-[#E76F51]/20 transition-all duration-200"
                  placeholder={t("auth.signup.emailPlaceholder")}
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="signup-password" className="block font-sans text-xs font-semibold text-[#0F172A] mb-1">
                  {t("auth.signup.passwordLabel")}
                </label>
                <div className="relative">
                  <input
                    id="signup-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={handlePasswordChange}
                    disabled={loading}
                    className="w-full px-3 py-2.5 bg-brand-light border-2 border-brand-text/10 rounded-lg font-sans text-sm text-[#0F172A] placeholder-brand-text/40 focus:outline-none focus:border-[#E76F51] focus:ring-2 focus:ring-[#E76F51]/20 transition-all duration-200 pr-10"
                    placeholder={t("auth.signup.passwordPlaceholder")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-text/40 hover:text-[#0F172A] transition-colors"
                    aria-label={t("auth.signup.showPassword")}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {/* Password strength bar */}
                <div className="mt-1.5 h-1 bg-brand-text/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${getStrengthColor()}`}
                    style={{ width: `${(passwordStrength / 4) * 100}%` }}
                  />
                </div>
                <p className="mt-1 font-sans text-xs text-brand-text/50">
                  {t("auth.signup.passwordHint")}
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 px-6 bg-[#E76F51] text-white font-sans font-semibold rounded-xl hover:bg-[#d4614a] shadow-lg shadow-[#E76F51]/25 hover:shadow-xl hover:shadow-[#E76F51]/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>{t("auth.signup.submitting")}</span>
                  </>
                ) : (
                  <>
                    <span>{t("auth.signup.submitButton")}</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>

            {/* Login Link */}
            <p className="mt-4 text-center font-sans text-sm text-brand-text/60">
              {t("auth.signup.hasAccount")}{" "}
              <button
                type="button"
                onClick={() => switchToLogin('signup-to-login')}
                className="font-semibold text-[#E76F51] hover:underline bg-transparent border-0 cursor-pointer"
              >
                {t("auth.signup.loginLink")}
              </button>
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SignupV3;
